import React from "react";
import { C } from "../theme";

// Shared refresh (circular-arrows) glyph used by every collapsible section that
// exposes a manual refresh. Spins while `busy` is true.
export function RefreshIcon({ size = 16, color = C.muted, busy = false }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        display: "block",
        animation: busy ? "the99-refresh-spin .9s linear infinite" : "none",
      }}
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

/**
 * Standardized collapsible section used across the whole app.
 *
 * Renders a bordered card (matching the Trip Report graphs style) with a
 * full-width header. The header shows an optional leading icon, the title,
 * an optional subtitle/right slot, an optional refresh button, and finally a
 * chevron (▾) that rotates -90deg when collapsed. When a refresh button is
 * present the chevron sits to its right, and both live inside the card border.
 *
 * Controlled: pass `open` and `onToggle`.
 */
export default function CollapsibleSection({
  title,
  icon = null,
  subtitle = null,
  headerRight = null,
  open,
  onToggle,
  onRefresh = null,
  refreshing = false,
  refreshTitle = "Refresh",
  refreshDisabled = false,
  glow = null,
  children,
  style = null,
  bodyStyle = null,
  headerTitle = null,
  toggleTestId = null,
  id = null,
  "data-testid": dataTestId,
}) {
  const borderColor = glow || C.border;
  return (
    <div
      id={id || undefined}
      data-testid={dataTestId}
      style={{
        background: C.cardBg,
        border: `1px solid ${borderColor}`,
        borderRadius: 14,
        overflow: "hidden",
        ...(glow ? { boxShadow: `0 0 0 1px ${glow}22` } : null),
        ...style,
      }}
    >
      <style>{"@keyframes the99-refresh-spin{to{transform:rotate(360deg)}}"}</style>
      <div style={{ display: "flex", alignItems: "center" }}>
        <button
          type="button"
          onClick={onToggle}
          title={headerTitle || (open ? "Collapse" : "Expand")}
          data-testid={toggleTestId || undefined}
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "13px 14px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          {icon != null && (
            <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>
          )}
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              fontWeight: 700,
              color: C.white,
              letterSpacing: 0.3,
            }}
          >
            {title}
            {subtitle != null && (
              <span style={{ color: C.muted, fontWeight: 500, fontSize: 11 }}>
                {" "}
                {subtitle}
              </span>
            )}
          </span>
          {headerRight}
        </button>

        {onRefresh && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!refreshing && !refreshDisabled) onRefresh(e);
            }}
            title={refreshTitle}
            aria-busy={refreshing}
            disabled={refreshing || refreshDisabled}
            style={{
              background: "transparent",
              border: "none",
              cursor: refreshing || refreshDisabled ? "default" : "pointer",
              padding: 6,
              display: "inline-flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <RefreshIcon color={refreshing ? C.seafoam : C.muted} busy={refreshing} />
          </button>
        )}

        <button
          type="button"
          onClick={onToggle}
          title={open ? "Collapse" : "Expand"}
          aria-expanded={open}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: onRefresh ? "6px 14px 6px 4px" : "6px 14px",
            display: "inline-flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              color: C.muted,
              fontSize: 13,
              transform: open ? "none" : "rotate(-90deg)",
              transition: "transform .2s",
            }}
          >
            ▾
          </span>
        </button>
      </div>

      {open && (
        <div style={{ padding: "0 14px 14px", ...bodyStyle }}>{children}</div>
      )}
    </div>
  );
}
